import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { matchesQuestion, questions as ALL_QUESTIONS } from '../data/questions';
import { sampleN, shuffle } from '../utils/random';
import { saveHighScore } from '../utils/localStorage';
import '../styles.css';

const QUESTIONS_PER_RUN = 10;
const SECONDS_PER_QUESTION = 12;
const RESULT_MESSAGES = [
  'Brain warmed up.',
  'Trivia goblin approved.',
  'Certified knowledge gremlin.',
  'You survived the quiz realm.',
];

function Quiz() {
  const { state } = useLocation();
  const selectedCategory = state?.category || 'All';
  const selectedDifficulty = state?.difficulty || 'Any';
  const selectedType = state?.type || 'Any';
  const selectedSearch = state?.search || '';

  const runQuestions = useMemo(() => {
    const pool = ALL_QUESTIONS.filter((question) =>
      matchesQuestion(question, {
        category: selectedCategory,
        difficulty: selectedDifficulty,
        type: selectedType,
        search: selectedSearch,
      })
    );

    return sampleN(pool, Math.min(QUESTIONS_PER_RUN, pool.length)).map((question) => ({
      ...question,
      _options: question.type === 'multiple' ? shuffle(question.options) : ['True', 'False'],
    }));
  }, [selectedCategory, selectedDifficulty, selectedSearch, selectedType]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(SECONDS_PER_QUESTION);
  const [feedback, setFeedback] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const answeredRef = useRef(false);
  const currentQuestion = runQuestions[currentIndex];
  const total = runQuestions.length;
  const maxScore = runQuestions.reduce((sum, question) => sum + (question.points || 10), 0);
  const percentage = maxScore ? Math.round((score / maxScore) * 100) : 0;
  const progressValue = quizComplete ? total : currentIndex;
  const resultMessage = RESULT_MESSAGES[Math.min(Math.floor(percentage / 25), RESULT_MESSAGES.length - 1)];

  useEffect(() => {
    if (quizComplete || !currentQuestion) return undefined;

    setTimeLeft(SECONDS_PER_QUESTION);
    setDisabled(false);
    setFeedback(null);
    setSelectedAnswer(null);
    answeredRef.current = false;

    const timer = setInterval(() => {
      setTimeLeft((previous) => {
        if (previous <= 1) {
          clearInterval(timer);
          if (!answeredRef.current) handleAnswer(null);
          return 0;
        }

        return previous - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [currentIndex, quizComplete, currentQuestion]);

  const moveNext = () => {
    if (currentIndex + 1 < total) {
      setCurrentIndex((previous) => previous + 1);
    } else {
      setQuizComplete(true);
    }
  };

  const handleAnswer = (selected) => {
    if (answeredRef.current || !currentQuestion) return;

    answeredRef.current = true;
    setDisabled(true);
    setSelectedAnswer(selected);

    const correct = selected === currentQuestion.answer;
    const questionPoints = currentQuestion.points || 10;

    if (correct) {
      setScore((previous) => previous + questionPoints);
      setCorrectCount((previous) => previous + 1);
    }

    setFeedback({
      correct,
      title: correct ? 'Correct' : selected === null ? 'Time is up' : 'Not quite',
      text: correct
        ? `Nice work. You earned ${questionPoints} points.`
        : `Correct answer: ${currentQuestion.answer}.`,
      insight: currentQuestion.insight,
    });
  };

  const handleNameSubmit = (event) => {
    event.preventDefault();
    const trimmed = name.trim().slice(0, 24);
    if (!trimmed) return;

    saveHighScore({
      name: trimmed,
      score,
      total: maxScore,
      correct: correctCount,
      questions: total,
      category: selectedCategory,
      difficulty: selectedDifficulty,
      type: selectedType,
    });

    setSubmitted(true);
  };

  const getButtonClass = (option) => {
    if (!disabled) return 'option-button button is-fullwidth';
    if (option === currentQuestion.answer) return 'option-button option-button--correct button is-fullwidth';
    if (option === selectedAnswer) return 'option-button option-button--wrong button is-fullwidth';
    return 'option-button option-button--muted button is-fullwidth';
  };

  if (total === 0) {
    return (
      <section className="section quiz-page">
        <div className="quiz-card empty-state">
          <h2 className="title is-4">No questions found</h2>
          <p>Try a different search, category, difficulty, or question type.</p>
          <Link to="/" className="button is-primary mt-4">
            Back to Start
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="section quiz-page">
      <div className="quiz-card quiz-container" aria-live="polite">
        {quizComplete ? (
          <div className="result-panel">
            <p className="eyebrow">Quiz complete</p>
            <h2 className="title is-3">{resultMessage}</h2>
            <p className="result-scoreline">You scored {percentage}%</p>
            <div className="result-stats">
              <div>
                <span>Score</span>
                <strong>
                  {score}/{maxScore}
                </strong>
              </div>
              <div>
                <span>Correct</span>
                <strong>
                  {correctCount}/{total}
                </strong>
              </div>
              <div>
                <span>Category</span>
                <strong>{selectedCategory === 'All' ? 'Mixed' : selectedCategory}</strong>
              </div>
              <div>
                <span>Difficulty</span>
                <strong>{selectedDifficulty === 'Any' ? 'Mixed' : selectedDifficulty}</strong>
              </div>
              <div>
                <span>Questions</span>
                <strong>{total}</strong>
              </div>
            </div>

            {!submitted ? (
              <form onSubmit={handleNameSubmit} className="save-score-form">
                <label className="label" htmlFor="player-name">
                  Save your score
                </label>
                <div className="field has-addons">
                  <div className="control is-expanded">
                    <input
                      id="player-name"
                      className="input"
                      type="text"
                      placeholder="Name or initials"
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      maxLength="24"
                    />
                  </div>
                  <div className="control">
                    <button className="button is-primary" type="submit">
                      Save
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <p className="save-confirmation">Score saved to the top-five board.</p>
            )}

            <div className="buttons is-centered">
              <Link to="/" className="button is-primary">
                Play Again
              </Link>
              <Link to="/" className="button is-light">
                Browse More Topics
              </Link>
              <Link to="/highscores" className="button is-light">
                High Scores
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className="quiz-meta">
              <span>{selectedCategory}</span>
              <span>{selectedDifficulty === 'Any' ? 'Mixed difficulty' : selectedDifficulty}</span>
              <span>{selectedType === 'Any' ? 'All types' : selectedType}</span>
              <span>{currentQuestion.points || 10} pts</span>
            </div>

            <div className="progress-row">
              <span>
                Question {currentIndex + 1} of {total}
              </span>
              <span>{timeLeft}s</span>
            </div>
            <progress className="progress is-primary" value={progressValue} max={total} />

            <h2 className="question title is-4">{currentQuestion.question}</h2>

            <div className="options">
              {currentQuestion._options.map((option) => (
                <button
                  key={option}
                  className={getButtonClass(option)}
                  onClick={() => handleAnswer(option)}
                  disabled={disabled}
                  type="button"
                >
                  {option}
                </button>
              ))}
            </div>

            {feedback && (
              <div className={`feedback ${feedback.correct ? 'is-correct' : 'is-wrong'}`}>
                <strong>{feedback.title}</strong>
                <p>{feedback.text}</p>
                <p className="insight">{feedback.insight}</p>
                <button className="button is-primary mt-3" type="button" onClick={moveNext}>
                  {currentIndex + 1 < total ? 'Next Question' : 'See Results'}
                </button>
              </div>
            )}

            <div className="score-row">
              <span>Score: {score}</span>
              <span>
                Correct: {correctCount}/{currentIndex + (disabled ? 1 : 0)}
              </span>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export default Quiz;
